import { createContext, useContext, useEffect, useState } from "react";
import api from "../utils/api";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("waec_token"));
  const [loading, setLoading] = useState(true);

  const sessionToken = localStorage.getItem("waec_session");

  // ===============================
  // REFRESH USER FROM BACKEND
  // ===============================
  const refreshUser = async () => {
    const res = await api.get("/auth/me");
    setUser(res.data.user);
    localStorage.setItem("waec_user", JSON.stringify(res.data.user));
  };

  // ===============================
  // LOGIN
  // ===============================
  const login = (userData, jwtToken, newSessionToken) => {
    setUser(userData);
    setToken(jwtToken);

    localStorage.setItem("waec_token", jwtToken);
    localStorage.setItem("waec_user", JSON.stringify(userData));

    if (newSessionToken) {
      localStorage.setItem("waec_session", newSessionToken);
    }
  };

  // ===============================
  // LOGOUT (NO HARD REFRESH)
  // ===============================
  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed:", err?.message);
    } finally {
      setUser(null);
      setToken(null);

      localStorage.removeItem("waec_token");
      localStorage.removeItem("waec_session");
      localStorage.removeItem("waec_user");
      // ❌ DO NOT reload page
    }
  };

  // ===============================
  // AUTH BOOTSTRAP (STABLE)
  // ===============================
  useEffect(() => {
    const bootstrapAuth = async () => {
      const storedUser = localStorage.getItem("waec_user");
      const storedToken = localStorage.getItem("waec_token");

      if (!storedToken) {
        setLoading(false);
        return;
      }

      // Optimistic restore
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
          setToken(storedToken);
        } catch {}
      }

      // Validate with backend
      try {
        const res = await api.get("/auth/me");
        setUser(res.data.user);
        localStorage.setItem("waec_user", JSON.stringify(res.data.user));
      } catch (err) {
        const status = err?.response?.status;

        // ❗ Only invalidate on REAL unauthorized
        if (status === 401) {
          console.warn("Auth expired – clearing session");
          setUser(null);
          setToken(null);
          localStorage.removeItem("waec_token");
          localStorage.removeItem("waec_user");
        } else {
          console.warn("Transient auth error ignored");
        }
      } finally {
        setLoading(false);
      }
    };

    bootstrapAuth();
  }, []);

  const hasActiveSubscription =
    user?.has_active_subscription === true;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        sessionToken,
        loading,
        login,
        logout,
        refreshUser,
        role: user?.role || null,
        hasActiveSubscription,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
